// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {useCallback} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import {readMultipleChannels} from 'mattermost-redux/actions/channels';

import {getUnreadChannels} from 'selectors/views/channel_sidebar';

import * as Menu from 'components/menu';

import CreateNewCategoryMenuItem from './sidebar_category/sidebar_category_menu/create_new_category_menu_item';
import MarkAsReadMenuItem from './sidebar_category/sidebar_category_menu/mark_as_read_menu_item';
import SidebarCategoryGenericMenu from './sidebar_category/sidebar_category_menu/sidebar_category_generic_menu';
import {SidebarCategoryHeaderStatic} from './sidebar_category_header';
import SidebarChannel from './sidebar_channel';

type Props = {
    setChannelRef: (channelId: string, ref: HTMLLIElement) => void;
};

export default function UnreadChannels({
    setChannelRef,
}: Props) {
    const intl = useIntl();
    const unreadChannels = useSelector(getUnreadChannels);
    const dispatch = useDispatch();

    const handleViewCategory = useCallback(() => {
        if (!unreadChannels.length) {
            return;
        }

        dispatch(readMultipleChannels(unreadChannels.map((v) => v.id)));
    }, [unreadChannels, dispatch]);

    if (unreadChannels.length === 0) {
        return null;
    }

    return (
        <div className='SidebarChannelGroup dropDisabled a11y__section'>
            <SidebarCategoryHeaderStatic displayName={intl.formatMessage({id: 'sidebar.types.unreadChannels.displayName', defaultMessage: 'UNREADS'})}>
                <SidebarCategoryGenericMenu
                    id='unreads'
                    name={intl.formatMessage({id: 'sidebar.types.unreadChannels.ariaLabel', defaultMessage: 'Unread'})}
                >
                    <MarkAsReadMenuItem
                        id={'unreads'}
                        handleViewCategory={handleViewCategory}
                        numChannels={unreadChannels.length}
                    />
                    <Menu.Separator/>
                    <CreateNewCategoryMenuItem id={'unreads'}/>
                </SidebarCategoryGenericMenu>
            </SidebarCategoryHeaderStatic>
            <div className='SidebarChannelGroup_content'>
                <ul
                    role='list'
                    className='NavGroupContent'
                >
                    {unreadChannels.map((channel, index) => {
                        return (
                            <SidebarChannel
                                key={channel.id}
                                channelIndex={index}
                                channelId={channel.id}
                                setChannelRef={setChannelRef}
                                isCategoryCollapsed={false}
                                isCategoryDragged={false}
                                isDraggable={false}
                                isAutoSortedCategory={true}
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
